import type { LocationPermissions } from '../../location-layer/types/location'

import type { AccessCondition } from '../types/app-access'

/**
 * @example
 * courses: {
 *   permission: 'membershipEnabled',
 *   conditions: [requirePermission('certificatesEnabled')],
 * }
 */
export const requirePermission = (key: keyof LocationPermissions): AccessCondition => {
  return ({ locationPermissions }) => locationPermissions[key] ?? false
}

/**
 * @example
 * documents: {
 *   permission: 'sharedFilesEnabled',
 *   conditions: [requireAllPermissions('membershipEnabled', 'contractsEnabled')],
 * }
 */
export const requireAllPermissions = (...keys: (keyof LocationPermissions)[]): AccessCondition => {
  return ({ locationPermissions }) => keys.every(key => locationPermissions[key] ?? false)
}

/**
 * @example
 * invoices: {
 *   permission: 'subscriptionsEnabled',
 *   conditions: [requireAnyPermission('membershipEnabled', 'contractsEnabled')],
 * }
 */
export const requireAnyPermission = (...keys: (keyof LocationPermissions)[]): AccessCondition => {
  return ({ locationPermissions }) => keys.some(key => locationPermissions[key] ?? false)
}

/**
 * Temporary Levelup-2026 gate: an app with this condition counts as inaccessible until the master
 * flag is on (preview-cache `featureFlags.clientPortal.isLevelUp2026Enabled` or the `cp-levelup-2026` cookie), which hides
 * its navigation entry and links and blocks its guarded routes. It is a UI rollout gate, not an
 * authorization boundary. Remove after launch.
 *
 * @example
 * discover: {
 *   permission: 'discoverEnabled',
 *   conditions: [requireLevelUp2026],
 * }
 */
export const requireLevelUp2026: AccessCondition = ({ isLevelUp2026Enabled }) => isLevelUp2026Enabled
